import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { navigate } from '../router';
import { getCurrentUser, navigateAfterLogin, stytch } from '../auth/session.ts';
import { LitPage } from '../components/lit-page.ts';

import '../components/luna-auth-card/luna-auth-card';
import '../components/luna-button/luna-button';

const SESSION_DURATION_MINUTES = 60 * 24 * 90; // 90 days

@customElement('app-login')
export class AppLogin extends LitPage {
  @state() private email = '';
  @state() private password = '';
  @state() private busy = false;
  @state() private errorMessage = '';
  @state() private noticeMessage = '';

  firstUpdated() {
    if (getCurrentUser()) {
      navigate('/');
    }
  }

  private async onSignIn(e: Event) {
    e.preventDefault();
    if (this.busy) return;
    await this.run(async () => {
      await stytch.passwords.authenticate({
        email: this.email,
        password: this.password,
        session_duration_minutes: SESSION_DURATION_MINUTES,
      });
      navigateAfterLogin();
    }, 'That email and password didn\'t match. Please try again.');
  }

  private async onForgotPassword() {
    if (this.busy) return;
    if (!this.email) {
      navigate('/auth/forgot-password');
      return;
    }
    await this.run(async () => {
      await stytch.passwords.resetByEmailStart({
        email: this.email,
        reset_password_redirect_url: `${window.location.origin}/auth/reset-password`,
      });
      this.noticeMessage = `We've emailed a password reset link to ${this.email}.`;
    }, 'We couldn\'t send the reset email. Please try again.');
  }

  private async onEmailLink() {
    if (this.busy) return;
    if (!this.email) {
      navigate('/auth/magic-link');
      return;
    }
    await this.run(async () => {
      await stytch.magicLinks.email.loginOrCreate(this.email, {
        login_magic_link_url: `${window.location.origin}/auth/callback`,
        login_expiration_minutes: 60,
        signup_magic_link_url: `${window.location.origin}/auth/callback`,
        signup_expiration_minutes: 60,
      });
      this.noticeMessage = `We've emailed a sign-in link to ${this.email}.`;
    }, 'We couldn\'t send the email. Please try again.');
  }

  private async run(action: () => Promise<void>, fallbackError: string) {
    this.busy = true;
    this.errorMessage = '';
    this.noticeMessage = '';
    try {
      await action();
    } catch (err) {
      const errorType = (err as { error_type?: string })?.error_type;
      this.errorMessage = errorType === 'email_not_found'
        ? 'That email address isn\'t registered. Please check it and try again.'
        : fallbackError;
    } finally {
      this.busy = false;
    }
  }

  content() {
    return html`
      <luna-auth-card>
        <h1 class="mbw-type-title-section">Sign in</h1>

        <div class="auth-error" role="alert" ?hidden=${!this.errorMessage}>
          ${this.errorMessage}
        </div>
        <div class="auth-notice" role="status" ?hidden=${!this.noticeMessage}>
          ${this.noticeMessage}
        </div>

        <form @submit=${this.onSignIn}>
          <div>
            <label for="login-email">Email address</label>
            <input
              id="login-email"
              type="email"
              autocomplete="email"
              required
              .value=${this.email}
              @input=${(e: Event) => { this.email = (e.target as HTMLInputElement).value; }}
            />
          </div>

          <div>
            <label for="login-password">Password</label>
            <input
              id="login-password"
              type="password"
              autocomplete="current-password"
              required
              .value=${this.password}
              @input=${(e: Event) => { this.password = (e.target as HTMLInputElement).value; }}
            />
          </div>

          <button type="submit" class="auth-submit-ghost" tabindex="-1" aria-hidden="true">
            Sign in
          </button>
          <luna-button full ?disabled=${this.busy} @click=${this.onSignIn}>
            <span>${this.busy ? 'Signing in…' : 'Sign in'}</span>
          </luna-button>
        </form>

        <div class="auth-links">
          <button
            class="auth-link"
            type="button"
            ?disabled=${this.busy}
            @click=${this.onForgotPassword}
          >
            Forgot your password?
          </button>
          <button
            class="auth-link"
            type="button"
            ?disabled=${this.busy}
            @click=${this.onEmailLink}
          >
            Email me a sign-in link instead
          </button>
        </div>
      </luna-auth-card>
    `;
  }
}
